import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Paramount palette
const OLIVE = '#3d4128';
const SAGE = '#b9c096';
const CREAM = '#fbf8f3';

const TYPE_META = {
  targeting_mismatch_fix:      { label: 'Fix Targeting',      bg: '#fff7ed', border: '#fdba74', accent: '#9a3412', icon: '\u26a0\ufe0f' },
  cannibalization_consolidate: { label: 'Consolidate Pages',  bg: '#fffbeb', border: '#fcd34d', accent: '#92400e', icon: '\u26a1' },
  ctr_fix:                     { label: 'Fix Click-Through',  bg: '#fefce8', border: '#facc15', accent: '#854d0e', icon: '\ud83c\udfaf' },
  content_gap_create:          { label: 'Create Page',        bg: '#fef2f2', border: '#fca5a5', accent: '#991b1b', icon: '\ud83d\udd0d' },
  striking_distance_push:      { label: 'Push Rankings',      bg: '#eff6ff', border: '#93c5fd', accent: '#1e40af', icon: '\ud83d\ude80' },
  authority_strengthen:        { label: 'Strengthen Authority', bg: '#f0fdf4', border: '#86efac', accent: '#166534', icon: '\ud83c\udf31' },
};

const COVERAGE_BADGE = {
  dedicated:   { label: 'Dedicated',   color: '#166534', bg: '#f0fdf4' },
  partial:     { label: 'Partial',     color: '#92400e', bg: '#fffbeb' },
  mismatch:    { label: 'Mismatch',    color: '#9a3412', bg: '#fff7ed' },
  content_gap: { label: 'Content Gap', color: '#991b1b', bg: '#fef2f2' },
  brand:       { label: 'Brand',       color: OLIVE,     bg: CREAM },
  no_audit:    { label: 'Unknown',     color: '#475569', bg: '#f8fafc' },
};

const BUCKET_META = [
  { key: 'top3',   label: 'Top 3',   color: '#166534' },
  { key: 'page1',  label: 'Page 1',  color: '#1e40af' },
  { key: 'page2',  label: 'Page 2',  color: '#854d0e' },
  { key: 'page3',  label: 'Page 3',  color: '#9a3412' },
  { key: 'beyond', label: 'Beyond',  color: '#64748b' },
];

function formatVol(v) {
  if (v === null || v === undefined) return '\u2014';
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return String(v);
}

function MoveCard({ move, onStatusChange }) {
  const [busy, setBusy] = useState(false);
  const meta = TYPE_META[move.prescription_type] || { label: move.prescription_type, bg: '#f8fafc', border: '#cbd5e1', accent: '#475569', icon: '\ud83d\udca1' };
  const ev = move.evidence || {};

  const setStatus = async (status) => {
    let reason = null;
    if (status === 'dismissed') {
      reason = window.prompt('Why dismiss this? (optional \u2014 helps improve future recommendations)');
      if (reason === null) return; // user cancelled the prompt
    }
    setBusy(true);
    try {
      const updated = await api.setPrescriptionStatus(move.id, status, reason || undefined);
      onStatusChange(updated);
    } catch (e) {
      alert('Could not update: ' + (e.response && e.response.data && e.response.data.message ? e.response.data.message : e.message));
    } finally {
      setBusy(false);
    }
  };

  const accepted = move.status === 'accepted';

  return (
    <div style={{ background: meta.bg, border: '1px solid ' + meta.border, borderLeft: '4px solid ' + meta.accent, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>{meta.icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 8px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
          {meta.label}
        </span>
        {typeof ev.opportunity_score === 'number' && (
          <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent, padding: '2px 8px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
            Opportunity {ev.opportunity_score}
          </span>
        )}
        {accepted && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', padding: '2px 8px', background: '#dcfce7', borderRadius: 4 }}>
            Accepted
          </span>
        )}
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>{move.title}</div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, marginBottom: 8 }}>{move.diagnosis}</div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, marginBottom: 10 }}>
        <span style={{ fontWeight: 700, color: meta.accent }}>What to do: </span>{move.action}
      </div>
      {Array.isArray(ev.competing_urls) && ev.competing_urls.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.55)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: meta.accent, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 5 }}>
            Ranking URLs ({ev.competing_urls.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {ev.competing_urls.map(u => (
              <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#374151', wordBreak: 'break-all' }}>{u}</a>
            ))}
          </div>
        </div>
      )}


      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        {move.keyword && <span><b>Keyword:</b> {move.keyword}</span>}
        {ev.volume !== null && ev.volume !== undefined && <span><b>Volume:</b> {formatVol(ev.volume)}/mo</span>}
        {ev.cpc !== null && ev.cpc !== undefined && <span><b>CPC:</b> ${Number(ev.cpc).toFixed(2)}</span>}
        {ev.position !== null && ev.position !== undefined && <span><b>Position:</b> {Number(ev.position).toFixed(1)}</span>}
        {move.target_url && (
          <span>
            <b>Page: </b>
            <a href={move.target_url} target="_blank" rel="noopener noreferrer" style={{ color: OLIVE, wordBreak: 'break-all' }}>{move.target_url}</a>
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {!accepted && (
          <button
            disabled={busy}
            onClick={() => setStatus('accepted')}
            style={{ padding: '7px 16px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}
          >
            Accept
          </button>
        )}
        <button
          disabled={busy}
          onClick={() => setStatus('dismissed')}
          style={{ padding: '7px 16px', background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default function Brief({ websiteId, globalDays }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const days = globalDays === 7 || globalDays === 90 ? globalDays : 28;

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBrief(websiteId, days);
      setBrief(data);
    } catch (e) {
      setError(e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId, days]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = (updated) => {
    setBrief(prev => {
      if (!prev) return prev;
      if (updated.status === 'dismissed') {
        return { ...prev, top_moves: prev.top_moves.filter(m => m.id !== updated.id) };
      }
      return { ...prev, top_moves: prev.top_moves.map(m => (m.id === updated.id ? updated : m)) };
    });
  };

  if (!websiteId) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Select a website to generate its Opportunity Brief.</div>;
  }
  if (loading && !brief) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Generating Opportunity Brief… analyzing keywords, coverage, and demand data.</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: '#991b1b', fontSize: 14, marginBottom: 12 }}>Brief generation failed: {error}</div>
        <button onClick={load} style={{ padding: '8px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (!brief) return null;

  const stand = brief.where_you_stand || {};
  const buckets = stand.position_buckets || {};
  const covDist = stand.coverage_distribution || {};
  const totalBucketed = BUCKET_META.reduce((s, b) => s + (buckets[b.key] || 0), 0);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, color: OLIVE, fontSize: 22 }}>Opportunity Brief</h2>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {brief.domain} · last {brief.window_days} days · generated {new Date(brief.generated_at + 'Z').toLocaleString()}
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 22 }}>
        Where you stand, where the money is, and your top moves — with the evidence behind each.
      </div>

      {/* ── Where You Stand ─────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: OLIVE, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>Where You Stand</div>

        {totalBucketed > 0 ? (
          <div>
            <div style={{ display: 'flex', height: 26, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
              {BUCKET_META.map(b => {
                const n = buckets[b.key] || 0;
                if (!n) return null;
                return (
                  <div key={b.key} title={b.label + ': ' + n} style={{ width: (n / totalBucketed * 100) + '%', background: b.color, minWidth: 4 }} />
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginBottom: 12 }}>
              {BUCKET_META.map(b => (
                <span key={b.key} style={{ color: '#374151' }}>
                  <span style={{ display: 'inline-block', width: 9, height: 9, background: b.color, borderRadius: 2, marginRight: 5 }} />
                  {b.label}: <b>{buckets[b.key] || 0}</b>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>No keywords passed the data threshold in this window.</div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(covDist).map(([status, n]) => {
            const b = COVERAGE_BADGE[status] || COVERAGE_BADGE.no_audit;
            return (
              <span key={status} style={{ fontSize: 12, fontWeight: 600, color: b.color, background: b.bg, padding: '4px 10px', borderRadius: 6 }}>
                {b.label}: {n}
              </span>
            );
          })}
        </div>

        <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>{stand.note}</div>
      </div>

      {/* ── Where The Money Is ──────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: OLIVE, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>Where The Money Is</div>
        {(brief.where_the_money_is || []).length === 0 ? (
          <div style={{ fontSize: 13, color: '#64748b' }}>No enriched keywords with volume data in this window. Run enrichment to populate this section.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                <th style={{ padding: '6px 8px' }}>Keyword</th>
                <th style={{ padding: '6px 8px' }}>Volume</th>
                <th style={{ padding: '6px 8px' }}>CPC</th>
                <th style={{ padding: '6px 8px' }}>KD</th>
                <th style={{ padding: '6px 8px' }}>Position</th>
                <th style={{ padding: '6px 8px' }}>Coverage</th>
                <th style={{ padding: '6px 8px' }}>Opportunity</th>
              </tr>
            </thead>
            <tbody>
              {brief.where_the_money_is.map(m => {
                const b = COVERAGE_BADGE[m.coverage_status] || COVERAGE_BADGE.no_audit;
                return (
                  <tr key={m.keyword} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '7px 8px', fontWeight: 600, color: '#1f2937' }}>{m.keyword}</td>
                    <td style={{ padding: '7px 8px' }}>{formatVol(m.volume)}</td>
                    <td style={{ padding: '7px 8px' }}>{m.cpc !== null && m.cpc !== undefined ? '$' + Number(m.cpc).toFixed(2) : '\u2014'}</td>
                    <td style={{ padding: '7px 8px' }}>{m.keyword_difficulty !== null && m.keyword_difficulty !== undefined ? m.keyword_difficulty : '\u2014'}</td>
                    <td style={{ padding: '7px 8px' }}>{m.position}</td>
                    <td style={{ padding: '7px 8px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: b.color, background: b.bg, padding: '2px 8px', borderRadius: 5 }}>{b.label}</span>
                    </td>
                    <td style={{ padding: '7px 8px', fontWeight: 700, color: OLIVE }}>{m.opportunity_score}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Top Moves ───────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: OLIVE, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Top Moves</div>
          {brief.more_moves_count > 0 && (
            <div style={{ fontSize: 12, color: '#64748b' }}>{brief.more_moves_count} more in the pipeline</div>
          )}
        </div>
        {(brief.top_moves || []).length === 0 ? (
          <div style={{ fontSize: 13, color: '#64748b' }}>
            No open moves. Either everything proposed has been handled, or there is not enough data yet — run a Site Audit and enrichment, then regenerate.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {brief.top_moves.map(move => (
              <MoveCard key={move.id} move={move} onStatusChange={handleStatusChange} />
            ))}
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <button onClick={load} disabled={loading} style={{ padding: '7px 16px', background: 'transparent', color: OLIVE, border: '1px solid ' + SAGE, borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? 'Regenerating\u2026' : 'Regenerate Brief'}
          </button>
        </div>
      </div>
    </div>
  );
}
